import {
  ActivitySchema,
  CreateActivityRequest,
  NeedsActivityDto,
  UpdateActivitySchema,
} from '@act/shared/data-transfer-objects';
import { FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { useForm } from 'react-hook-form';
import {
  createNeedsMetadataSuccess,
  updateNeedsMetadataSuccess,
} from 'redux/slices/metaSlice';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/store-hooks';
import { useApiCall } from 'redux/utils/api-hook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import { z } from 'zod';
import { useToast } from '../../../../app/ReusableToast';
import { AdminDrawerForm } from '../../AdminDrawer';

interface Props {
  open: boolean;
  setOpen: (value: boolean) => void;
  isEdit: boolean;
  selectedActivity?: NeedsActivityDto;
}

export const NeedsActivityCreateEdit = ({
  open,
  setOpen,
  isEdit,
  selectedActivity,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const schema = isEdit ? UpdateActivitySchema : ActivitySchema;
  type ActivityFormData = z.infer<typeof schema>;
  const { control, handleSubmit, formState } = useForm<ActivityFormData>({
    resolver: zodResolver(schema),
    defaultValues: selectedActivity
      ? {
          key: selectedActivity.key ?? '',
          label: selectedActivity.label ?? '',
          overview: selectedActivity.overview ?? '',
          defaultTimeFrameInMinutes:
            selectedActivity.defaultTimeFrameInMinutes ?? 0,
          iconName: selectedActivity.iconName ?? '',
        }
      : {
          key: '',
          label: '',
          overview: '',
          defaultTimeFrameInMinutes: 0,
          iconName: '',
        },
  });
  const [needActivityResult, createNeedActivityLoading, createNeedActivity] =
    useApiCall(needRequests.createNeedsMetadata);
  const [
    updateNeedActivityResult,
    updateNeedActivityLoading,
    updateNeedActivity,
  ] = useApiCall(needRequests.updateNeedsMetadata);

  const formConfig: FormConfig<ActivityFormData> = [
    {
      type: 'input',
      name: 'label',
      label: 'Activity Label',
      input: 'text',
    },
    {
      type: 'input',
      name: 'overview',
      label: 'Activity Overview',
      input: 'text',
    },
    {
      type: 'input',
      name: 'defaultTimeFrameInMinutes',
      label: 'Default Time Frame',
      input: 'number',
    },
    {
      type: 'input',
      name: 'iconName',
      label: 'Icon Name',
      input: 'text',
    },
    {
      type: 'input',
      name: 'key',
      label: 'Key',
      input: 'text',
    },
  ];

  const onSubmit = (data: ActivityFormData) => {
    if (isEdit) {
      void updateNeedActivity({
        body: {
          ...data,
        },
        params: { type: 'activities', id: selectedActivity?.id },
      });
    } else {
      void createNeedActivity({
        body: {
          ...(data as CreateActivityRequest),
        },
        params: { type: 'activities' },
      });
    }
  };
  // Handle successful creation
  useEffect(() => {
    if (needActivityResult && !createNeedActivityLoading && open) {
      dispatch(
        createNeedsMetadataSuccess({
          data: needActivityResult,
          type: 'activities',
        })
      );
      toast('Activity created successfully');
      setOpen(false);
    }
  }, [needActivityResult, createNeedActivityLoading]);

  // Handle successful update
  useEffect(() => {
    if (updateNeedActivityResult && !updateNeedActivityLoading && open) {
      dispatch(
        updateNeedsMetadataSuccess({
          data: updateNeedActivityResult,
          type: 'activities',
        })
      );
      toast('Activity updated successfully');
      setOpen(false);
    }
  }, [updateNeedActivityResult, updateNeedActivityLoading]);
  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };
  const DrawerContent = useMemo(() => {
    return (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    );
  }, [control, formConfig, formState.errors]);

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Activity` : `Add New Activity`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
